import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Star, Brush, Palette, Heart, Wand2, Camera, Music, Cake,
  Check, ArrowRight, PartyPopper, ShieldCheck, Loader2, Gift, ArrowUpRight, ChevronDown,
} from "lucide-react";
import { API } from "@/lib/auth";
import { track } from "@/lib/analytics";

const ICONS = {
  sparkles: Sparkles, star: Star, brush: Brush, palette: Palette,
  heart: Heart, wand: Wand2, camera: Camera, music: Music, cake: Cake,
};

export default function PamperParties() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    parent_name: "", email: "", phone: "",
    child_name: "", child_age: "",
    package: "", party_date: "", party_time: "",
    children_count: "",
    party_bags: "unsure", party_bags_qty: "",
    allergies: "", additional_needs: "", special_requests: "",
    referral: "", notes: "", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/pamper-parties/content`)
      .then((r) => setContent(r.data))
      .catch((err) => {
        console.warn("Failed to load pamper-party content", err);
        toast.error("Couldn't load the page — please refresh");
      })
      .finally(() => setLoading(false));
  }, []);

  const scrollToForm = (preselectPackage) => {
    if (preselectPackage) setForm((f) => ({ ...f, package: preselectPackage }));
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    try { track("pamper_party_cta_click", { package: preselectPackage || "hero" }); } catch (err) { console.warn("track failed", err); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.consent) {
      toast.error("Please tick the consent box before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        children_count: form.children_count ? Number(form.children_count) : null,
        party_bags_qty: form.party_bags === "yes" && form.party_bags_qty
          ? Number(form.party_bags_qty) : null,
      };
      const r = await axios.post(`${API}/pamper-parties/enquire`, payload);
      setReference(r.data?.reference);
      try { track("pamper_party_enquiry", { package: form.package || "unspecified" }); } catch (err) { console.warn("track failed", err); }
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your enquiry — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-40 pb-40 flex items-center justify-center text-[hsl(var(--onyx)/0.5)]">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }
  if (!content) return null;

  if (reference) return <PamperSuccess success={content.success} reference={reference} />;

  return (
    <div data-testid="page-pamper-parties" className="pt-24 pb-24 bg-gradient-to-b from-[hsl(var(--blush)/0.4)] via-white to-white">
      <PamperHero hero={content.hero} onEnquire={() => scrollToForm(null)} />
      <PamperIncluded items={content.included} note={content.included_note} />
      <PamperGallery items={content.gallery} heading={content.gallery_heading} />
      <PamperPackages packages={content.packages} onEnquire={scrollToForm} addon={content.addon} />
      <PamperAddon addon={content.addon} />
      <div ref={formRef} className="scroll-mt-24">
        <PamperForm
          content={content}
          form={form}
          setForm={setForm}
          submitting={submitting}
          onSubmit={submit}
        />
      </div>
      <PamperFinalCta finalCta={content.final_cta} onEnquire={() => scrollToForm(null)} />
    </div>
  );
}


// ============================================================
// Hero
// ============================================================
function PamperHero({ hero, onEnquire }) {
  return (
    <section className="mx-auto max-w-[1240px] px-5 sm:px-10 pt-8 sm:pt-16">
      <div className="grid gap-10 lg:grid-cols-12 items-center">
        <div className="lg:col-span-7 relative">
          <div className="overline text-[hsl(var(--burgundy))] inline-flex items-center gap-2">
            <PartyPopper size={12} /> {hero.eyebrow}
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-balance">
            {hero.headline?.split("magical").length > 1
              ? <>{hero.headline.split("magical")[0]}<span className="italic font-light">magical</span>{hero.headline.split("magical")[1]}</>
              : hero.headline}
          </h1>
          <p className="mt-6 text-[hsl(var(--onyx)/0.7)] max-w-xl text-lg leading-relaxed">
            {hero.subheading}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={onEnquire}
              data-testid="pamper-hero-cta"
              className="btn-pill btn-primary"
            >
              {hero.primary_cta || "Enquire Now"} <ArrowRight size={16} />
            </button>
            <a href="#packages" data-testid="pamper-hero-packages" className="btn-pill btn-outline">
              See packages
            </a>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {(hero.stats || [
              { label: "Ages", value: "3 – 14" },
              { label: "From", value: "£30 / child" },
              { label: "Duration", value: "2 – 3 hrs" },
            ]).map((s, i) => (
              <MiniStat key={i} label={s.label} value={s.value} />
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative">
            <div className="aspect-[4/5] rounded-[2.5rem] bg-blush overflow-hidden shadow-[0_30px_80px_-40px_rgba(139,27,58,0.35)]">
              {hero.image && (
                <img src={hero.image} alt="Pamper party" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blush flex items-center justify-center">
                <Sparkles size={16} className="text-[hsl(var(--burgundy))]" />
              </div>
              <div>
                <div className="overline text-[hsl(var(--gold))]">Fully bespoke</div>
                <div className="text-sm text-[hsl(var(--onyx))]">Tailored to your group</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="font-display text-xl mt-1">{value}</div>
    </div>
  );
}


// ============================================================
// What's Included
// ============================================================
function PamperIncluded({ items, note }) {
  return (
    <section className="mx-auto max-w-[1100px] px-5 sm:px-10 mt-24">
      <div className="text-center">
        <div className="overline text-[hsl(var(--burgundy))]">Every party includes</div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-balance">
          A little bit of <span className="italic font-light">everything</span> they love.
        </h2>
      </div>
      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(items || []).map((it, i) => {
          const Icon = ICONS[it.icon] || Sparkles;
          return (
            <div
              key={`${it.icon}-${it.label}-${i}`}
              data-testid={`pamper-included-${i}`}
              className="group rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-full bg-blush flex items-center justify-center group-hover:bg-[hsl(var(--burgundy))] group-hover:text-[hsl(var(--cream))] transition-colors">
                <Icon size={18} className="text-[hsl(var(--burgundy))] group-hover:text-[hsl(var(--cream))] transition-colors" />
              </div>
              <div className="mt-4 font-display text-lg leading-tight">{it.label}</div>
            </div>
          );
        })}
      </div>
      {note && (
        <p className="mt-12 text-center max-w-2xl mx-auto text-[hsl(var(--onyx)/0.7)] leading-relaxed">
          {note}
        </p>
      )}
    </section>
  );
}


// ============================================================
// Recent parties mini gallery
// ============================================================
function PamperGallery({ items, heading }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="mx-auto max-w-[1240px] px-5 sm:px-10 mt-24">
      <div className="text-center">
        <div className="overline text-[hsl(var(--burgundy))]">Recent parties</div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-balance">
          {heading || "Little moments from our recent parties"}
        </h2>
      </div>
      <div
        data-testid="pamper-gallery"
        className="mt-12 flex gap-4 sm:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth px-2 -mx-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((it, i) => (
          <div
            key={`${it.type}-${it.url || i}`}
            data-testid={`pamper-gallery-${i}`}
            className="snap-start shrink-0 rounded-[2rem] overflow-hidden bg-blush shadow-[0_20px_60px_-30px_rgba(139,27,58,0.4)] hover:-translate-y-1 hover:shadow-[0_30px_80px_-30px_rgba(139,27,58,0.55)] transition-all duration-500 relative group w-[70vw] sm:w-[300px]"
          >
            <div className="aspect-[3/4] relative">
              {it.type === "video" ? (
                <>
                  <video
                    src={it.url}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster={it.poster || undefined}
                    className="absolute inset-0 w-full h-full object-cover bg-blush"
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-14 h-14 rounded-full bg-[hsl(var(--cream)/0.15)] backdrop-blur-md flex items-center justify-center border border-[hsl(var(--cream)/0.3)] group-hover:scale-110 transition-transform">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[hsl(var(--cream))]">
                        <path d="M8 5v14l11-7z" fill="currentColor" />
                      </svg>
                    </div>
                  </div>
                </>
              ) : (
                <img
                  src={it.url}
                  alt={it.caption || "Pamper party moment"}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />
              {it.caption && (
                <div className="absolute bottom-4 left-5 right-5 text-[hsl(var(--cream))]">
                  <div className="overline text-[hsl(var(--gold))] text-[10px]">
                    {it.type === "video" ? "▶ Watch" : "Party moment"}
                  </div>
                  <div className="font-display text-lg leading-tight mt-1">{it.caption}</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-center text-xs text-[hsl(var(--onyx)/0.5)] sm:hidden">
        Swipe to see more →
      </p>
    </section>
  );
}


// ============================================================
// Packages
// ============================================================
function PamperPackages({ packages, onEnquire, addon }) {
  return (
    <section id="packages" className="mx-auto max-w-[1100px] px-5 sm:px-10 mt-24 scroll-mt-24">
      <div className="text-center">
        <div className="overline text-[hsl(var(--burgundy))]">Party packages</div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-balance">
          Pick your <span className="italic font-light">perfect party</span>.
        </h2>
      </div>
      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        {(packages || []).map((p) => (
          <div
            key={p.id}
            data-testid={`pamper-package-${p.id}`}
            className={`relative rounded-[2.5rem] p-8 sm:p-10 border transition-all duration-300 hover:-translate-y-1 ${
              p.featured
                ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] border-transparent shadow-[0_40px_100px_-40px_rgba(139,27,58,0.5)]"
                : "bg-white text-[hsl(var(--onyx))] border-[hsl(var(--onyx)/0.08)] shadow-[0_30px_80px_-40px_rgba(20,10,15,0.15)]"
            }`}
          >
            {p.featured && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[hsl(var(--gold))] text-[hsl(var(--onyx))] text-[10px] uppercase tracking-[0.28em]">
                Most popular
              </div>
            )}
            <div className={`overline ${p.featured ? "text-[hsl(var(--gold))]" : "text-[hsl(var(--burgundy))]"}`}>
              {p.duration_hours} hours
            </div>
            <h3 className="mt-3 font-display text-3xl leading-tight">{p.name}</h3>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-5xl">£{p.price_per_child}</span>
              <span className={`text-sm ${p.featured ? "text-[hsl(var(--cream)/0.6)]" : "text-[hsl(var(--onyx)/0.55)]"}`}>
                per child
              </span>
            </div>
            {(p.min_children || p.max_children) && (
              <div className={`mt-2 text-xs ${p.featured ? "text-[hsl(var(--cream)/0.55)]" : "text-[hsl(var(--onyx)/0.5)]"}`}>
                {p.min_children && `Min ${p.min_children}`}
                {p.min_children && p.max_children && " · "}
                {p.max_children && `Max ${p.max_children} children`}
              </div>
            )}
            <ul className="mt-6 space-y-3">
              {(p.highlights || []).map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-sm leading-relaxed">
                  <div className={`shrink-0 mt-1 w-4 h-4 rounded-full flex items-center justify-center ${
                    p.featured ? "bg-[hsl(var(--gold))]" : "bg-[hsl(var(--burgundy))]"
                  }`}>
                    <Check size={10} className={p.featured ? "text-[hsl(var(--onyx))]" : "text-[hsl(var(--cream))]"} />
                  </div>
                  <span className={p.featured ? "text-[hsl(var(--cream)/0.85)]" : "text-[hsl(var(--onyx)/0.75)]"}>
                    {h}
                  </span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => onEnquire(p.name)}
              data-testid={`pamper-package-cta-${p.id}`}
              className={`mt-8 w-full h-12 rounded-full font-medium text-sm transition-opacity hover:opacity-90 inline-flex items-center justify-center gap-2 ${
                p.featured
                  ? "bg-[hsl(var(--gold))] text-[hsl(var(--onyx))]"
                  : "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
              }`}
            >
              {p.cta || "Enquire About This Package"} <ArrowRight size={14} />
            </button>
          </div>
        ))}
      </div>
      {addon && (
        <p className="mt-8 text-center text-xs text-[hsl(var(--onyx)/0.55)]">
          Optional add-on: {addon.name} · £{addon.price_per_child} per child
        </p>
      )}
    </section>
  );
}


// ============================================================
// Add-on
// ============================================================
function PamperAddon({ addon }) {
  if (!addon) return null;
  return (
    <section className="mx-auto max-w-[1000px] px-5 sm:px-10 mt-24">
      <div className="rounded-[2.5rem] bg-blush/60 border border-[hsl(var(--onyx)/0.06)] p-8 sm:p-12 grid gap-8 lg:grid-cols-12 items-center">
        <div className="lg:col-span-4">
          <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-sm">
            <Gift size={22} className="text-[hsl(var(--burgundy))]" />
          </div>
          <div className="overline mt-4 text-[hsl(var(--burgundy))]">Optional add-on</div>
          <h3 className="font-display text-3xl mt-2">{addon.name}</h3>
          <div className="mt-2 font-display text-2xl">£{addon.price_per_child} <span className="text-sm text-[hsl(var(--onyx)/0.55)] font-sans">per child</span></div>
          {addon.description && (
            <p className="mt-4 text-sm text-[hsl(var(--onyx)/0.7)] leading-relaxed">{addon.description}</p>
          )}
        </div>
        <div className="lg:col-span-8">
          <div className="overline">Each bag includes</div>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {(addon.contents || []).map((c, i) => (
              <li key={i} className="flex items-center gap-3 text-sm text-[hsl(var(--onyx)/0.8)]">
                <div className="shrink-0 w-2 h-2 rounded-full bg-[hsl(var(--gold))]" /> {c}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}


// ============================================================
// Enquiry Form
// ============================================================
function PamperForm({ content, form, setForm, submitting, onSubmit }) {
  const { packages = [], addon, referral_options = [], consent_label, final_cta } = content;
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  return (
    <section className="mx-auto max-w-[900px] px-5 sm:px-10 mt-24">
      <div className="text-center">
        <div className="overline text-[hsl(var(--burgundy))]">Enquire now</div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-balance">
          Tell us about your <span className="italic font-light">little celebration</span>.
        </h2>
        <p className="mt-4 text-[hsl(var(--onyx)/0.65)] max-w-lg mx-auto text-sm leading-relaxed">
          Fill in a few details and we'll get back to you within 2–3 working days with availability and next steps.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        data-testid="pamper-form"
        className="mt-12 rounded-[2.5rem] bg-white border border-[hsl(var(--onyx)/0.06)] p-8 sm:p-10 shadow-[0_30px_80px_-40px_rgba(20,10,15,0.15)] space-y-8"
      >
        <FieldGroup title="About you">
          <Grid>
            <Field label="Parent / guardian name" required>
              <Input type="text" required value={form.parent_name} onChange={(v) => update({ parent_name: v })} testid="pamper-parent-name" />
            </Field>
            <Field label="Email" required>
              <Input type="email" required value={form.email} onChange={(v) => update({ email: v })} testid="pamper-email" />
            </Field>
            <Field label="Phone" required>
              <Input type="tel" required value={form.phone} onChange={(v) => update({ phone: v })} testid="pamper-phone" />
            </Field>
          </Grid>
        </FieldGroup>

        <FieldGroup title="About the birthday star">
          <Grid>
            <Field label="Child's name" required>
              <Input type="text" required value={form.child_name} onChange={(v) => update({ child_name: v })} testid="pamper-child-name" />
            </Field>
            <Field label="Child's age" required>
              <Input type="text" required placeholder="e.g. 7" value={form.child_age} onChange={(v) => update({ child_age: v })} testid="pamper-child-age" />
            </Field>
            <Field label="Number of children attending">
              <Input type="number" min="1" value={form.children_count} onChange={(v) => update({ children_count: v })} testid="pamper-children-count" />
            </Field>
          </Grid>
        </FieldGroup>

        <FieldGroup title="About the party">
          <Grid>
            <Field label="Preferred package">
              <Select value={form.package} onChange={(v) => update({ package: v })} testid="pamper-package">
                <option value="">Choose one…</option>
                {packages.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name} — £{p.price_per_child} per child
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Preferred date">
              <Input type="date" value={form.party_date} onChange={(v) => update({ party_date: v })} testid="pamper-date" />
            </Field>
            <Field label="Preferred time">
              <Input type="time" value={form.party_time} onChange={(v) => update({ party_time: v })} testid="pamper-time" />
            </Field>
          </Grid>
        </FieldGroup>

        <FieldGroup title={`Party bags · £${addon?.price_per_child || 6} per child`}>
          <div className="flex gap-2 flex-wrap">
            {[
              { v: "yes", l: "Yes please" },
              { v: "no", l: "No thanks" },
              { v: "unsure", l: "Unsure" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => update({ party_bags: opt.v })}
                data-testid={`pamper-bags-${opt.v}`}
                className={`px-5 py-2.5 rounded-full text-sm border transition-colors ${
                  form.party_bags === opt.v
                    ? "border-[hsl(var(--burgundy))] bg-blush text-[hsl(var(--burgundy))]"
                    : "border-[hsl(var(--onyx)/0.15)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--burgundy)/0.4)]"
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          {form.party_bags === "yes" && (
            <div className="mt-4 max-w-xs">
              <Field label="How many party bags?">
                <Input type="number" min="1" value={form.party_bags_qty} onChange={(v) => update({ party_bags_qty: v })} testid="pamper-bags-qty" />
              </Field>
            </div>
          )}
        </FieldGroup>

        <FieldGroup title="Extra info" collapsible>
          <div className="space-y-4">
            <Field label="Do any children have allergies or dietary requirements?">
              <Textarea value={form.allergies} onChange={(v) => update({ allergies: v })} testid="pamper-allergies" />
            </Field>
            <Field label="Any additional needs we should be aware of?">
              <Textarea value={form.additional_needs} onChange={(v) => update({ additional_needs: v })} testid="pamper-needs" />
            </Field>
            <Field label="Special requests or party theme">
              <Textarea value={form.special_requests} onChange={(v) => update({ special_requests: v })} testid="pamper-theme" />
            </Field>
            <Grid>
              <Field label="How did you hear about us?">
                <Select value={form.referral} onChange={(v) => update({ referral: v })} testid="pamper-referral">
                  <option value="">Select…</option>
                  {(referral_options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </Select>
              </Field>
              <Field label="Anything else you'd like us to know?">
                <Input type="text" value={form.notes} onChange={(v) => update({ notes: v })} testid="pamper-notes" />
              </Field>
            </Grid>
          </div>
        </FieldGroup>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            required
            checked={form.consent}
            onChange={(e) => update({ consent: e.target.checked })}
            data-testid="pamper-consent"
            className="mt-1 w-5 h-5 rounded border-[hsl(var(--onyx)/0.3)] text-[hsl(var(--burgundy))] focus:ring-[hsl(var(--burgundy))]"
          />
          <span className="text-sm text-[hsl(var(--onyx)/0.75)] leading-relaxed">
            {consent_label || "I understand this is an enquiry only and my booking is not confirmed until contacted."}
          </span>
        </label>

        <div className="flex items-center gap-2 text-xs text-[hsl(var(--onyx)/0.5)]">
          <ShieldCheck size={12} /> Your details are stored securely and only used to plan your party.
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="pamper-submit"
          className="w-full h-14 rounded-full bg-[hsl(var(--burgundy))] text-[hsl(var(--cream))] font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {submitting
            ? <><Loader2 size={16} className="animate-spin" /> Sending your enquiry…</>
            : <>{final_cta?.button || "Send my enquiry"} <ArrowRight size={16} /></>}
        </button>
      </form>
    </section>
  );
}


// ============================================================
// Success page
// ============================================================
function PamperSuccess({ success, reference }) {
  return (
    <section data-testid="pamper-success" className="pt-32 pb-32 px-5 sm:px-10 bg-gradient-to-b from-[hsl(var(--blush)/0.4)] via-white to-white min-h-[80vh] flex items-center justify-center">
      <div className="max-w-lg text-center">
        <div className="mx-auto w-20 h-20 rounded-full bg-blush flex items-center justify-center">
          <PartyPopper size={30} className="text-[hsl(var(--burgundy))]" />
        </div>
        <div className="mt-6 overline text-[hsl(var(--gold))]">Reference {reference}</div>
        <h1 className="mt-4 font-display text-5xl sm:text-6xl tracking-tight text-balance">
          {success?.headline || "Thank You!"}
        </h1>
        <p className="mt-6 text-[hsl(var(--onyx)/0.7)] leading-relaxed">
          {success?.body}
        </p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link to="/" data-testid="pamper-success-home" className="btn-pill btn-outline">
            Back to home
          </Link>
          <Link to="/gallery" data-testid="pamper-success-gallery" className="btn-pill btn-primary">
            See our gallery <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}


// ============================================================
// Final CTA
// ============================================================
function PamperFinalCta({ finalCta, onEnquire }) {
  if (!finalCta) return null;
  return (
    <section className="mx-auto max-w-[1000px] px-5 sm:px-10 mt-24">
      <div className="rounded-[2.5rem] bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] p-10 sm:p-16 text-center">
        <div className="overline text-[hsl(var(--gold))]">Sparkle · smiles · memories</div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-balance">
          {finalCta.headline}
        </h2>
        <p className="mt-4 text-[hsl(var(--cream)/0.7)] max-w-xl mx-auto text-sm leading-relaxed">
          {finalCta.body}
        </p>
        <button
          onClick={onEnquire}
          data-testid="pamper-final-cta"
          className="mt-8 btn-pill btn-blush"
        >
          {finalCta.button || "Send my enquiry"} <PartyPopper size={16} />
        </button>
      </div>
    </section>
  );
}


// ============================================================
// Field helpers
// ============================================================
function FieldGroup({ title, children, collapsible = false }) {
  const [open, setOpen] = useState(!collapsible);
  return (
    <div>
      <button
        type="button"
        onClick={collapsible ? () => setOpen((o) => !o) : undefined}
        className={`w-full text-left overline flex items-center justify-between ${collapsible ? "cursor-pointer" : "cursor-default"}`}
      >
        <span>{title}</span>
        {collapsible && (
          <ChevronDown
            size={14}
            className={`text-[hsl(var(--onyx)/0.4)] transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {open && <div className="mt-4">{children}</div>}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="overline">{label} {required && <span className="text-[hsl(var(--burgundy))]">*</span>}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Input({ testid, type = "text", value, onChange, ...rest }) {
  return (
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full h-11 rounded-full bg-white border border-black/10 px-5 text-sm outline-none focus:border-[hsl(var(--burgundy))] transition-colors"
      {...rest}
    />
  );
}

function Textarea({ testid, value, onChange, rows = 3 }) {
  return (
    <textarea
      rows={rows}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full rounded-2xl bg-white border border-black/10 px-4 py-3 text-sm outline-none focus:border-[hsl(var(--burgundy))] transition-colors resize-none"
    />
  );
}

function Select({ testid, value, onChange, children }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full h-11 rounded-full bg-white border border-black/10 px-5 text-sm outline-none focus:border-[hsl(var(--burgundy))] transition-colors appearance-none cursor-pointer"
      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238b1b3a' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 1rem center" }}
    >
      {children}
    </select>
  );
}
